import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import {
  getFreightActiveUserCreate,
  getFreightActiveUserEdit,
  storeFreightActiveUser,
  updateFreightActiveUser,
  getFreightNewUserEdit,
  updateFreightNewUser,
  searchFreightLocations,
  verifyFreightGst,
  checkFreightGstNumber,
  checkFreightTransNumber,
  checkFreightActiveMobile,
  checkFreightActivePan,
  checkFreightActiveAadhaar
} from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/toast";
import { pickExistingFileUrl } from "../../../utils/fileUploadHelpers";
import FileUploadWithPreview from "../../../components/common/FileUploadWithPreview";
import "./adminFreight.css";

const emptyForm = () => ({
  name: "",
  country_id: "",
  area_id: "",
  mobile_no: "",
  gst: "",
  eway_trans_id: "",
  gst_pincode: "",
  gst_state: "",
  gst_city: "",
  email: "",
  company_name: "",
  address: "",
  pan_number: "",
  aadhaar_number: "",
  otp: ""
});

const FILE_ORDER = [
  "profile_picture",
  "pan_front_image",
  "pan_back_image",
  "aadhaar_front_image",
  "aadhaar_back_image"
];

const FILE_LABELS = {
  profile_picture: "Profile picture",
  pan_front_image: "PAN — front",
  pan_back_image: "PAN — back",
  aadhaar_front_image: "Aadhaar — front",
  aadhaar_back_image: "Aadhaar — back"
};

const CREATE_REQUIRED_FILES = new Set(["pan_front_image", "aadhaar_front_image"]);

const GST_IN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/i;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

function validateFreightUserForm(form, { isCreate, files, existingFileUrls }) {
  const errors = {};

  const name = (form.name ?? "").trim();
  if (name.length < 2) {
    errors.name = "Enter full name (at least 2 characters).";
  }

  if (!String(form.country_id ?? "").trim()) {
    errors.country_id = "Select a country.";
  }

  if (!String(form.area_id ?? "").trim()) {
    errors.area_id = "Search and select a service area.";
  }

  const mobileDigits = String(form.mobile_no ?? "").replace(/\D/g, "");
  if (!mobileDigits.length) {
    errors.mobile_no = "Mobile number is required.";
  } else if (mobileDigits.length < 8 || mobileDigits.length > 15) {
    errors.mobile_no = "Enter a valid mobile number (8–15 digits).";
  }

  const email = (form.email ?? "").trim();
  if (email && !EMAIL_REGEX.test(email)) {
    errors.email = "Enter a valid email address.";
  }

  const gst = (form.gst ?? "").replace(/\s/g, "");
  if (gst && (gst.length !== 15 || !GST_IN_REGEX.test(gst))) {
    errors.gst = "Enter a valid 15-character GSTIN (e.g. 22AAAAA0000A1Z5).";
  }

  const pan = (form.pan_number ?? "").trim().toUpperCase();
  if (pan && !PAN_REGEX.test(pan)) {
    errors.pan_number = "Invalid PAN (e.g. ABCDE1234F).";
  }

  const aadhaarDigits = String(form.aadhaar_number ?? "").replace(/\D/g, "");
  if (aadhaarDigits && aadhaarDigits.length !== 12) {
    errors.aadhaar_number = "Aadhaar must be exactly 12 digits.";
  }

  if (isCreate) {
    if (!files.pan_front_image && !existingFileUrls?.pan_front_image) {
      errors.pan_front_image = "PAN front image is required.";
    }
    if (!files.aadhaar_front_image && !existingFileUrls?.aadhaar_front_image) {
      errors.aadhaar_front_image = "Aadhaar front image is required.";
    }
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

const FreightUserFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isCreate = location.pathname.includes("/freight/users/new");
  const isNewPipeline = location.pathname.includes("/new_users/");
  const userId = id;

  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = [
    { label: roleLabel, link: "/dashboard" },
    { label: "Freight", link: "/freight" },
    isCreate
      ? { label: "Create user" }
      : { label: `Edit user ${userId}` }
  ];

  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [locSearch, setLocSearch] = useState("");
  const [locResults, setLocResults] = useState([]);
  const [areaLabel, setAreaLabel] = useState("");
  const [files, setFiles] = useState({
    profile_picture: null,
    pan_front_image: null,
    pan_back_image: null,
    aadhaar_front_image: null,
    aadhaar_back_image: null
  });
  /** Server file URLs/paths for edit-mode preview (see `pickExistingFileUrl`). */
  const [existingFileUrls, setExistingFileUrls] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    if (isCreate) setExistingFileUrls({});
  }, [isCreate]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getFreightActiveUserCreate();
        setCountries(res?.data?.countries ?? []);
      } catch {
        setCountries([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (isCreate || !userId) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const res = isNewPipeline
          ? await getFreightNewUserEdit(userId)
          : await getFreightActiveUserEdit(userId);
        const u = res?.data?.userData ?? {};
        setForm((f) => ({
          ...f,
          name: u.name ?? "",
          country_id: u.country_id ?? u.country_code_id ?? "",
          area_id: u.area_id ?? "",
          mobile_no: u.mobile_no ?? "",
          gst: u.gst ?? "",
          eway_trans_id: u.eway_trans_id ?? "",
          gst_pincode: u.gst_pincode ?? "",
          gst_state: u.gst_state ?? "",
          gst_city: u.gst_city ?? "",
          email: u.email ?? "",
          company_name: u.company_name ?? "",
          address: u.address ?? "",
          pan_number: u.pan_number ?? "",
          aadhaar_number: u.aadhaar_number ?? "",
          otp: u.otp ?? ""
        }));
        setAreaLabel(
          [u.area_name, u.city_name, u.state_name].filter(Boolean).join(", ") || ""
        );
        const urls = {};
        FILE_ORDER.forEach((key) => {
          urls[key] = pickExistingFileUrl(u, key);
        });
        setExistingFileUrls(urls);
      } catch (e) {
        showError(e.message || "Failed to load user");
      } finally {
        setLoading(false);
      }
    })();
  }, [isCreate, userId, isNewPipeline]);

  const onSearchArea = async () => {
    try {
      const res = await searchFreightLocations({ search_text: locSearch, page_size: 30 });
      const areas = res?.data?.areas ?? [];
      setLocResults(areas);
    } catch (e) {
      showError(e.message || "Location search failed");
    }
  };

  const pickArea = (a) => {
    setForm((f) => ({ ...f, area_id: String(a.area_id) }));
    setAreaLabel(a.label || a.area_name);
    setLocResults([]);
    clearFieldError("area_id");
  };

  const runVerifyGst = async () => {
    if (!form.gst?.trim()) {
      showError("Enter GST first");
      return;
    }
    try {
      const q = { gst: form.gst.trim() };
      if (userId) q.user_id = userId;
      const r = await verifyFreightGst(q);
      if (!r.success) {
        showError(r.message || "GST verify failed");
        return;
      }
      setForm((f) => ({
        ...f,
        company_name: r.company_name ?? f.company_name,
        address: r.address ?? f.address,
        gst_city: r.gst_city ?? f.gst_city,
        gst_state: r.gst_state ?? f.gst_state,
        gst_pincode: r.gst_pincode ?? f.gst_pincode
      }));
      showSuccess(r.message || "GST verified");
    } catch (e) {
      showError(e.message || "GST verify failed");
    }
  };

  const appendForm = (fd) => {
    Object.entries(form).forEach(([k, v]) => {
      if (v != null && v !== "") fd.append(k, String(v));
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const validation = validateFreightUserForm(form, {
      isCreate,
      files,
      existingFileUrls
    });
    if (!validation.ok) {
      setFieldErrors(validation.errors);
      const firstMsg = Object.values(validation.errors)[0];
      if (firstMsg) showError(firstMsg);
      return;
    }
    setFieldErrors({});

    setSaving(true);
    try {
      if (isCreate && form.gst?.trim()) {
        const dupGst = await checkFreightGstNumber({ gst: form.gst.trim() });
        if (dupGst?.exists) {
          showError("GST already registered");
          setSaving(false);
          return;
        }
      }

      const fd = new FormData();
      appendForm(fd);
      if (!isCreate && userId) fd.append("user_id", String(userId));

      Object.entries(files).forEach(([key, file]) => {
        if (file) fd.append(key, file);
      });

      if (isCreate) {
        await storeFreightActiveUser(fd);
        showSuccess("User created");
        navigate("/freight/active_users");
      } else if (isNewPipeline) {
        await updateFreightNewUser(fd);
        showSuccess("User updated");
        navigate("/freight/new_users");
      } else {
        await updateFreightActiveUser(fd);
        showSuccess("User updated");
        navigate("/freight/active_users");
      }
    } catch (err) {
      showError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
        <p className="freight-admin-muted">Loading…</p>
      </DashboardLayout>
    );
  }

  const gstMetaLine = [form.gst_city, form.gst_state, form.gst_pincode]
    .filter(Boolean)
    .join(", ");

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="freight-admin-shell freight-admin-shell--narrow">
        <div className="card offers-table">
          <div className="freight-form-page-header">
            <div>
              <h3 className="freight-user-form-heading">
                {isCreate ? "Create freight user" : "Edit freight user"}
              </h3>
              <p className="freight-form-lead">
                {isCreate
                  ? "Add a fleet user with valid area, contact, and required document images."
                  : isNewPipeline
                    ? "Update this pending user before approval."
                    : "Update profile, tax details, and documents as needed."}
              </p>
            </div>
            <button type="button" className="btn-outline" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>

          <form className="freight-admin-form" onSubmit={submit} noValidate>
            <div className="freight-form-section">
              <h4 className="freight-form-section-title">Account & location</h4>
              <div className="freight-form-grid-2">
                <div className="form-row">
                  <label htmlFor="fu-name">Full name *</label>
                  <input
                    id="fu-name"
                    required
                    className={fieldErrors.name ? "freight-input-invalid" : undefined}
                    aria-invalid={Boolean(fieldErrors.name)}
                    value={form.name}
                    onChange={(e) => {
                      clearFieldError("name");
                      setForm({ ...form, name: e.target.value });
                    }}
                    autoComplete="name"
                  />
                  {fieldErrors.name ? (
                    <span className="freight-field-error">{fieldErrors.name}</span>
                  ) : null}
                </div>
                <div className="form-row">
                  <label htmlFor="fu-country">Country *</label>
                  <select
                    id="fu-country"
                    required
                    className={fieldErrors.country_id ? "freight-input-invalid" : undefined}
                    aria-invalid={Boolean(fieldErrors.country_id)}
                    value={form.country_id}
                    onChange={(e) => {
                      clearFieldError("country_id");
                      setForm({ ...form, country_id: e.target.value });
                    }}
                  >
                    <option value="">Select country</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name || c.country_name || c.id}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.country_id ? (
                    <span className="freight-field-error">{fieldErrors.country_id}</span>
                  ) : null}
                </div>
                <div className="form-row span-2">
                  <label htmlFor="fu-area-search">Service area *</label>
                  <span className="field-hint">Search by area name or pincode, then pick a result.</span>
                  <div className="freight-form-inline">
                    <input
                      id="fu-area-search"
                      type="text"
                      placeholder="Type area or pincode"
                      value={locSearch}
                      onChange={(e) => setLocSearch(e.target.value)}
                    />
                    <button type="button" className="btn-outline" onClick={onSearchArea}>
                      Search
                    </button>
                  </div>
                  {form.area_id && areaLabel && (
                    <div className="freight-area-selected">
                      <span>
                        Selected: <strong>#{form.area_id}</strong>
                      </span>
                      <span>— {areaLabel}</span>
                    </div>
                  )}
                  {locResults.length > 0 && (
                    <div className="freight-location-results">
                      {locResults.map((a) => (
                        <button
                          key={`${a.area_id}-${a.label}`}
                          type="button"
                          onClick={() => pickArea(a)}
                        >
                          {a.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {fieldErrors.area_id ? (
                    <span className="freight-field-error">{fieldErrors.area_id}</span>
                  ) : null}
                </div>
                <div className="form-row">
                  <label htmlFor="fu-mobile">Mobile *</label>
                  <input
                    id="fu-mobile"
                    required
                    inputMode="tel"
                    className={fieldErrors.mobile_no ? "freight-input-invalid" : undefined}
                    aria-invalid={Boolean(fieldErrors.mobile_no)}
                    value={form.mobile_no}
                    onChange={(e) => {
                      clearFieldError("mobile_no");
                      setForm({ ...form, mobile_no: e.target.value });
                    }}
                    onBlur={async () => {
                      if (!form.mobile_no || !form.country_id) return;
                      try {
                        const r = await checkFreightActiveMobile({
                          mobile_no: form.mobile_no,
                          country_code_id: String(form.country_id)
                        });
                        if (r.exists) showError("Mobile already exists");
                      } catch {
                        /* optional */
                      }
                    }}
                  />
                  {fieldErrors.mobile_no ? (
                    <span className="freight-field-error">{fieldErrors.mobile_no}</span>
                  ) : null}
                </div>
                <div className="form-row">
                  <label htmlFor="fu-email">Email</label>
                  <input
                    id="fu-email"
                    type="email"
                    className={fieldErrors.email ? "freight-input-invalid" : undefined}
                    aria-invalid={Boolean(fieldErrors.email)}
                    value={form.email}
                    onChange={(e) => {
                      clearFieldError("email");
                      setForm({ ...form, email: e.target.value });
                    }}
                    autoComplete="email"
                  />
                  {fieldErrors.email ? (
                    <span className="freight-field-error">{fieldErrors.email}</span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="freight-form-section">
              <h4 className="freight-form-section-title">Tax & company</h4>
              <div className="freight-form-grid-2">
                <div className="form-row span-2">
                  <label htmlFor="fu-gst">GST number</label>
                  <div className="freight-form-inline">
                    <input
                      id="fu-gst"
                      value={form.gst}
                      maxLength={15}
                      className={fieldErrors.gst ? "freight-input-invalid" : undefined}
                      aria-invalid={Boolean(fieldErrors.gst)}
                      onChange={(e) => {
                        clearFieldError("gst");
                        setForm({ ...form, gst: e.target.value.toUpperCase() });
                      }}
                      placeholder="e.g. 22AAAAA0000A1Z5"
                    />
                    <button type="button" className="btn-outline" onClick={runVerifyGst}>
                      Verify GST
                    </button>
                    <button
                      type="button"
                      className="btn-outline"
                      onClick={async () => {
                        try {
                          const r = await checkFreightGstNumber({
                            gst: form.gst,
                            user_id: userId
                          });
                          showSuccess(r.exists ? "GST exists" : "GST available");
                        } catch (err) {
                          showError(err.message);
                        }
                      }}
                    >
                      Check duplicate
                    </button>
                  </div>
                  {fieldErrors.gst ? (
                    <span className="freight-field-error">{fieldErrors.gst}</span>
                  ) : null}
                  {gstMetaLine ? (
                    <p className="freight-admin-muted" style={{ marginTop: "8px", marginBottom: 0 }}>
                      From GST: {gstMetaLine}
                    </p>
                  ) : null}
                </div>
                <div className="form-row span-2">
                  <label htmlFor="fu-eway">E-way transport ID</label>
                  <div className="freight-form-inline">
                    <input
                      id="fu-eway"
                      value={form.eway_trans_id}
                      onChange={(e) => setForm({ ...form, eway_trans_id: e.target.value })}
                    />
                    <button
                      type="button"
                      className="btn-outline"
                      onClick={async () => {
                        try {
                          const r = await checkFreightTransNumber({
                            eway_trans_id: form.eway_trans_id,
                            user_id: userId
                          });
                          showSuccess(r.exists ? "Exists" : "Available");
                        } catch (err) {
                          showError(err.message);
                        }
                      }}
                    >
                      Check ID
                    </button>
                  </div>
                </div>
                <div className="form-row span-2">
                  <label htmlFor="fu-company">Company name</label>
                  <input
                    id="fu-company"
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  />
                </div>
                <div className="form-row span-2">
                  <label htmlFor="fu-address">Address</label>
                  <textarea
                    id="fu-address"
                    rows={3}
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="freight-form-section">
              <h4 className="freight-form-section-title">Identity</h4>
              <div className="freight-form-grid-2">
                <div className="form-row">
                  <label htmlFor="fu-pan">PAN</label>
                  <input
                    id="fu-pan"
                    value={form.pan_number}
                    maxLength={10}
                    className={fieldErrors.pan_number ? "freight-input-invalid" : undefined}
                    aria-invalid={Boolean(fieldErrors.pan_number)}
                    onChange={(e) => {
                      clearFieldError("pan_number");
                      setForm({ ...form, pan_number: e.target.value.toUpperCase() });
                    }}
                    onBlur={async () => {
                      if (!form.pan_number) return;
                      try {
                        const r = await checkFreightActivePan({
                          pan_number: form.pan_number,
                          user_id: userId
                        });
                        if (r.exists) showError("PAN already used");
                      } catch {
                        /* */
                      }
                    }}
                  />
                  {fieldErrors.pan_number ? (
                    <span className="freight-field-error">{fieldErrors.pan_number}</span>
                  ) : null}
                </div>
                <div className="form-row">
                  <label htmlFor="fu-aadhaar">Aadhaar</label>
                  <input
                    id="fu-aadhaar"
                    inputMode="numeric"
                    value={form.aadhaar_number}
                    maxLength={14}
                    className={fieldErrors.aadhaar_number ? "freight-input-invalid" : undefined}
                    aria-invalid={Boolean(fieldErrors.aadhaar_number)}
                    onChange={(e) => {
                      clearFieldError("aadhaar_number");
                      const v = e.target.value.replace(/\D/g, "").slice(0, 12);
                      setForm({ ...form, aadhaar_number: v });
                    }}
                    onBlur={async () => {
                      if (!form.aadhaar_number) return;
                      try {
                        const r = await checkFreightActiveAadhaar({
                          aadhaar_number: form.aadhaar_number,
                          user_id: userId
                        });
                        if (r.exists) showError("Aadhaar already used");
                      } catch {
                        /* */
                      }
                    }}
                  />
                  {fieldErrors.aadhaar_number ? (
                    <span className="freight-field-error">{fieldErrors.aadhaar_number}</span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="freight-form-section">
              <h4 className="freight-form-section-title">Documents</h4>
              <p className="field-hint" style={{ margin: "-8px 0 14px" }}>
                {isCreate
                  ? "PAN front and Aadhaar front are required for new users. Other files are optional."
                  : "Upload only when replacing a file. Empty fields keep the existing document on the server."}
              </p>
              <div className="freight-doc-grid">
                {FILE_ORDER.map((key) => (
                  <div key={key} className="freight-doc-field">
                    <FileUploadWithPreview
                      id={`fu-doc-${key}`}
                      label={FILE_LABELS[key] || key}
                      file={files[key]}
                      onFileChange={(f) => {
                        clearFieldError(key);
                        setFiles((prev) => ({ ...prev, [key]: f }));
                      }}
                      existingUrl={existingFileUrls[key] || null}
                      required={isCreate && CREATE_REQUIRED_FILES.has(key)}
                      accept=".jpeg,.jpg,.png,.gif,.webp,.pdf"
                    />
                    {fieldErrors[key] ? (
                      <span className="freight-field-error">{fieldErrors[key]}</span>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? "Saving…" : isCreate ? "Create user" : "Save changes"}
              </button>
              <button type="button" className="btn-outline" onClick={() => navigate(-1)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightUserFormPage;
